import { useCallback, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { ContextDataItem } from '../../../types/widget';
import {
  saveUserSettingsPreferences,
  serializePersistableContextSelections,
} from '../../../utils/userPreferences';

export const useContextSelection = () => {
  const { sessionModel, updateSessionModel, user } = useContext(AppContext);

  const findItem = useCallback(
    (key: string | number) => {
      let item = sessionModel.contextData.find((item) => item.key === key);
      if (item) return item;

      for (const parentItem of sessionModel.contextData) {
        if (parentItem.children) {
          item = parentItem.children.find((child) => child.key === key);
          if (item) return item;
        }
      }
      return null;
    },
    [sessionModel.contextData]
  );

  const isSelected = useCallback(
    (key: string | number): boolean => {
      const item = findItem(key);
      return item?.selected ?? false;
    },
    [findItem]
  );

  const toggleSelection = useCallback(
    (key: string | number) => {
      updateSessionModel((prev) => {
        const newContextData = prev.contextData.map(
          (parentItem: ContextDataItem) => {
            if (parentItem.key === key) {
              const newSelected = !parentItem.selected;
              return {
                ...parentItem,
                selected: newSelected,
                children: parentItem.children?.map((child) => ({
                  ...child,
                  selected: newSelected,
                })),
              };
            }

            if (parentItem.children) {
              const updatedChildren = parentItem.children.map((child) =>
                child.key === key
                  ? { ...child, selected: !child.selected }
                  : child
              );

              const anyChildSelected = updatedChildren.some(
                (child) => child.selected
              );

              return {
                ...parentItem,
                children: updatedChildren,
                selected: anyChildSelected,
              };
            }

            return parentItem;
          }
        );

        // Save persistable selections to localStorage
        const userId = user?.username || user?.email;
        if (userId) {
          const hasPersistableItems = newContextData.some(
            (item) => item.persistPreviousSelection
          );
          if (hasPersistableItems) {
            const selections =
              serializePersistableContextSelections(newContextData);
            saveUserSettingsPreferences(userId, {
              lastContextSelections: selections,
            });
          }
        }

        return {
          ...prev,
          contextData: newContextData,
        };
      });
    },
    [updateSessionModel, user]
  );

  const updateContextData = useCallback(
    (key: string, value: unknown, options?: { hidden?: boolean }) => {
      updateSessionModel((prev) => {
        const existingItemIndex = prev.contextData.findIndex(
          (item) => item.key === key
        );

        let updatedContextData: ContextDataItem[];

        if (existingItemIndex >= 0) {
          updatedContextData = prev.contextData.map((item, index) =>
            index === existingItemIndex ? { ...item, value } : item
          );
        } else {
          updatedContextData = [
            ...prev.contextData,
            { key, value, hidden: options?.hidden },
          ];
        }

        return {
          ...prev,
          contextData: updatedContextData,
        };
      });
    },
    [updateSessionModel]
  );

  return {
    isSelected,
    toggleSelection,
    updateContextData,
    contextData: sessionModel.contextData,
  };
};
